/**
 * Query keys and cache lifetimes, in one place.
 *
 * Both halves are here for the same reason: they are the parts of a TanStack Query setup that
 * only work if every call site agrees. A key assembled at two call sites eventually disagrees
 * about one field and silently becomes two cache entries for one request; a `staleTime` chosen
 * per call site eventually becomes no `staleTime` at all.
 */
import { filterCacheKey, type CatalogFilters, type CatalogQuery } from '../lib/filters.js';

/**
 * The catalog is loaded by an ingestion run, not by users. It is correct for hours, not seconds.
 *
 * `STALE_CATALOG` covers anything a filter changes — the list and the facet counts. The lower
 * number is not about correctness, it is about a facet count being visibly stale while someone
 * works the sidebar.
 */
export const STALE_CATALOG = 5 * 60_000;
/** Course detail, pathways, lessons and transcripts. Immutable between ingestion runs. */
export const STALE_CONTENT = 30 * 60_000;
/** Typeahead. Long enough to make backspacing free, short enough to stay honest. */
export const STALE_SUGGEST = 60_000;
/**
 * The session.
 *
 * Deliberately short: the server can end a session at any time — a role change, a password
 * change, an admin deactivating the account — and the cached user is what the admin nav and the
 * transcript gate are drawn from.
 */
export const STALE_SESSION = 30_000;
/**
 * The admin area.
 *
 * Short, and for a different reason than the rest: these screens are about state an admin is
 * actively changing — a role, an invitation, a session — and a stale user table is a table
 * somebody acts on twice. Long enough that paging back and forth is free.
 */
export const STALE_ADMIN = 10_000;

export const queryKeys = {
  authConfig: () => ['auth', 'config'] as const,
  me: () => ['auth', 'me'] as const,
  invite: (token: string) => ['auth', 'invite', token] as const,

  courseList: (query: CatalogQuery) =>
    [
      'courses',
      'list',
      { ...filterCacheKey(query), q: query.q ?? null, sort: query.sort, page: query.page },
    ] as const,
  /** Facets ignore `q`, `sort` and `page` by design, so paging does not refetch the sidebar. */
  facets: (filters: CatalogFilters) => ['courses', 'facets', filterCacheKey(filters)] as const,
  suggest: (q: string) => ['courses', 'suggest', q] as const,
  course: (idOrIsbnOrSlug: string) => ['courses', 'detail', idOrIsbnOrSlug] as const,

  pathwayList: () => ['pathways', 'list'] as const,
  pathway: (idOrSlug: string) => ['pathways', 'detail', idOrSlug] as const,

  lesson: (id: string) => ['lessons', 'detail', id] as const,
} as const;

/**
 * The admin keys, separate from {@link queryKeys} so `['admin']` invalidates the whole area.
 *
 * Every mutation in the admin screens affects more than the list it was issued from — promoting
 * a user changes the role facet of the table *and* the session count, and accepting an invite
 * adds a user — so the blunt instrument is the correct one here. These are a handful of requests
 * made by one person at a time; the cost of refetching too much is nothing, and the cost of
 * refetching too little is an admin acting on a row that no longer says what the screen says.
 *
 * The filter object is embedded whole rather than reduced to a hash. TanStack Query compares keys
 * structurally, so the object *is* the key — and keeping it legible means a devtools session
 * shows which filter set a cache entry belongs to.
 */
export const adminKeys = {
  all: () => ['admin'] as const,
  users: (query: object) => ['admin', 'users', query] as const,
  invites: () => ['admin', 'invites'] as const,
  content: () => ['admin', 'content'] as const,
  queries: (filters: object) => ['admin', 'queries', filters] as const,
  /**
   * Keyed on everything except the page.
   *
   * The aggregates describe the whole filtered set, so paging the table beneath them must not
   * refetch or re-render them.
   */
  queryStats: (filters: { readonly page: number }) => {
    const { page: _page, ...rest } = filters;

    return ['admin', 'queries', 'stats', rest] as const;
  },
} as const;
